import { DEFAULT_PRICES, PAID_PLANS, PLANS, type LocaleId } from '@bannermatic/core';

/** Schema.org building blocks for JSON-LD. All URLs are absolute (site + path). */

export function organization(site: string) {
  return {
    '@type': 'Organization',
    '@id': `${site}/#organization`,
    name: 'Bannermatic',
    url: `${site}/`,
    logo: `${site}/icon-512.png`,
    sameAs: ['https://www.instagram.com/bannermatic.app'],
  };
}

export function website(site: string, locale: LocaleId) {
  return {
    '@type': 'WebSite',
    '@id': `${site}/#website`,
    name: 'Bannermatic',
    alternateName: ['Bannermatic App', 'bannermatic.app'],
    url: `${site}/`,
    inLanguage: locale,
    publisher: { '@id': `${site}/#organization` },
  };
}

/** The product with its real plan prices (USD), from free to the top plan. */
export function softwareApplication(site: string, description: string) {
  const prices = PAID_PLANS.map((p) => DEFAULT_PRICES.USD[p].month / 100);
  return {
    '@type': 'SoftwareApplication',
    '@id': `${site}/#software`,
    name: 'Bannermatic',
    url: `${site}/`,
    applicationCategory: 'DesignApplication',
    applicationSubCategory: 'Banner and image generation API',
    operatingSystem: 'Web',
    description,
    publisher: { '@id': `${site}/#organization` },
    offers: {
      '@type': 'AggregateOffer',
      priceCurrency: 'USD',
      lowPrice: 0,
      highPrice: Math.max(...prices),
      offerCount: Object.keys(PLANS).length,
    },
  };
}

export function faqPage(items: Array<{ q: string; a: string }>) {
  return {
    '@type': 'FAQPage',
    mainEntity: items.map(({ q, a }) => ({
      '@type': 'Question',
      name: q,
      acceptedAnswer: { '@type': 'Answer', text: a },
    })),
  };
}

export function breadcrumbs(items: Array<{ name: string; url: string }>) {
  return {
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: item.name,
      item: item.url,
    })),
  };
}

export function webPage(
  site: string,
  url: string,
  name: string,
  description: string,
  locale: LocaleId,
) {
  return {
    '@type': 'WebPage',
    '@id': url,
    url,
    name,
    description,
    inLanguage: locale,
    isPartOf: { '@id': `${site}/#website` },
  };
}

/** The contact page with the support address as the organization's contact point. */
export function contactPage(site: string, url: string, name: string, email: string) {
  return {
    '@type': 'ContactPage',
    '@id': url,
    url,
    name,
    isPartOf: { '@id': `${site}/#website` },
    about: {
      '@id': `${site}/#organization`,
      contactPoint: {
        '@type': 'ContactPoint',
        contactType: 'customer support',
        email,
        availableLanguage: ['English', 'Ukrainian', 'Polish', 'Romanian', 'Spanish', 'Russian'],
      },
    },
  };
}

export function itemList(items: Array<{ name: string; url: string }>) {
  return {
    '@type': 'ItemList',
    itemListElement: items.map((item, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: item.name,
      url: item.url,
    })),
  };
}
